import { useEffect, useRef } from 'react';

/**
 * Global scroll lock when any MUI Modal is open (Dialog, Menu, Drawer, Popover).
 * Locks html, body, and #root (no padding-right) so with scrollbarGutter: 'stable'
 * there is no layout shift, while the background no longer scrolls behind modals.
 */
function getScrollContainers(): { el: HTMLElement; key: string }[] {
    const list: { el: HTMLElement; key: string }[] = [
        { el: document.documentElement, key: 'html' },
        { el: document.body, key: 'body' },
    ];
    const root = document.getElementById('root');
    if (root) list.push({ el: root, key: 'root' });
    return list;
}

export function GlobalScrollLock() {
    const saved = useRef<Record<string, string>>({});
    const locked = useRef(false);

    useEffect(() => {
        const lock = () => {
            getScrollContainers().forEach(({ el, key }) => {
                saved.current[key] = el.style.overflow || '';
                el.style.overflow = 'hidden';
            });
            locked.current = true;
        };

        const unlock = () => {
            getScrollContainers().forEach(({ el, key }) => {
                el.style.overflow = saved.current[key] ?? '';
            });
            saved.current = {};
            locked.current = false;
        };

        const check = () => {
            const modals = document.querySelectorAll('.MuiModal-root, [class*="MuiModal-root"], [class*="MuiDialog-root"]');
            const hasOpen = modals.length > 0;

            if (hasOpen && !locked.current) {
                lock();
            } else if (!hasOpen && locked.current) {
                unlock();
            }
        };

        const runCheck = () => {
            requestAnimationFrame(() => {
                check();
            });
        };

        runCheck();
        const observer = new MutationObserver(runCheck);
        observer.observe(document.body, { childList: true, subtree: true });

        const interval = setInterval(runCheck, 200);

        return () => {
            observer.disconnect();
            clearInterval(interval);
            if (locked.current) unlock();
        };
    }, []);

    return null;
}
